import React, { useState, useEffect } from "react";
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Alert, 
  Spinner,
  Modal,
  Badge
} from "react-bootstrap";
import { FaSave, FaUndo, FaTrash, FaToggleOn, FaToggleOff, FaPlus } from "react-icons/fa";

const BannerSectionAdmin = () => {
  const [bannerData, setBannerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [newWord, setNewWord] = useState("");

  useEffect(() => {
    fetchBannerData();
  }, []);

  const fetchBannerData = async () => {
    try {
      const response = await fetch("https://ecom-22-fe6311cae608.herokuapp.com/api/banner-section");
      const data = await response.json();
      
      if (data.success) {
        setBannerData(data.data);
      } else {
        setError(data.message || "Failed to fetch banner section data");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
      console.error("Error fetching banner section:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBannerData({
      ...bannerData,
      [name]: value
    });
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setBannerData({
      ...bannerData,
      [name]: parseInt(value) || 0
    });
  };

  const handleAddWord = () => {
    if (newWord.trim()) {
      setBannerData({
        ...bannerData,
        typingWords: [...bannerData.typingWords, newWord.trim()]
      });
      setNewWord("");
    }
  };

  const handleRemoveWord = (index) => {
    const updatedWords = [...bannerData.typingWords];
    updatedWords.splice(index, 1);
    setBannerData({
      ...bannerData,
      typingWords: updatedWords
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch("https://ecom-22-fe6311cae608.herokuapp.com/api/banner-section", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(bannerData)
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess("Banner section updated successfully!");
        setBannerData(data.data);
      } else {
        setError(data.message || "Failed to update banner section");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
      console.error("Error updating banner section:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      const response = await fetch("https://ecom-22-fe6311cae608.herokuapp.com/api/banner-section/toggle", {
        method: "PATCH"
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Banner section ${data.data.isActive ? "activated" : "deactivated"} successfully!`);
        setBannerData(data.data);
      } else {
        setError(data.message || "Failed to toggle banner section status");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
      console.error("Error toggling banner section:", err);
    }
  };

  const handleReset = async () => {
    try {
      const response = await fetch("https://ecom-22-fe6311cae608.herokuapp.com/api/banner-section/reset", {
        method: "POST"
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess("Banner section reset to default successfully!");
        setBannerData(data.data);
        setShowResetModal(false);
      } else {
        setError(data.message || "Failed to reset banner section");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
      console.error("Error resetting banner section:", err);
    }
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden"></span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!bannerData) {
    return (
      <Container className="py-5">
        <Alert variant="danger">Failed to load banner section data</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <h2 className="mb-4">Manage Banner Section</h2>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Section Status</h5>
          <Button 
            variant={bannerData.isActive ? "success" : "secondary"}
            onClick={handleToggleActive}
          >
            {bannerData.isActive ? <FaToggleOn /> : <FaToggleOff />} 
            {bannerData.isActive ? " Active" : " Inactive"}
          </Button>
        </Card.Header>
        <Card.Body>
          <p>
            Last updated: {new Date(bannerData.updatedAt).toLocaleString()}
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Banner Content</h5>
        </Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Subtitle</Form.Label>
            <Form.Control
              type="text"
              name="subTitle"
              value={bannerData.subTitle}
              onChange={handleInputChange}
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Main Title</Form.Label>
            <Form.Control
              type="text"
              name="mainTitle"
              value={bannerData.mainTitle}
              onChange={handleInputChange}
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name="description"
              value={bannerData.description}
              onChange={handleInputChange}
            />
          </Form.Group>
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Button Text</Form.Label>
                <Form.Control
                  type="text"
                  name="buttonText"
                  value={bannerData.buttonText}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Button Link</Form.Label>
                <Form.Control
                  type="text"
                  name="buttonLink"
                  value={bannerData.buttonLink}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>
          
          <Form.Group className="mb-3">
            <Form.Label>Image URL</Form.Label>
            <Form.Control
              type="text"
              name="imageUrl"
              value={bannerData.imageUrl}
              onChange={handleInputChange}
              placeholder="https://example.com/image.jpg"
            />
            {bannerData.imageUrl && (
              <div className="mt-2">
                <img 
                  src={bannerData.imageUrl} 
                  alt="Banner Preview" 
                  style={{ maxWidth: "200px", maxHeight: "150px" }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://picsum.photos/seed/error/200/150.jpg";
                  }}
                />
              </div>
            )}
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>YouTube URL</Form.Label>
            <Form.Control
              type="text"
              name="youtubeUrl"
              value={bannerData.youtubeUrl}
              onChange={handleInputChange}
              placeholder="https://www.youtube.com/embed/VIDEO_ID"
            />
            <Form.Text className="text-muted">
              Leave empty to hide the video section
            </Form.Text>
          </Form.Group>
        </Card.Body>
      </Card>

      

      <div className="d-flex justify-content-between">
        <Button variant="secondary" onClick={() => setShowResetModal(true)}>
          <FaUndo /> Reset to Default
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span className="visually-hidden"></span>
            </>
          ) : (
            <><FaSave /> Save Changes</>
          )}
        </Button>
      </div>

      <Modal show={showResetModal} onHide={() => setShowResetModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reset to Default</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to reset the banner section to default values? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResetModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleReset}>
            Reset
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default BannerSectionAdmin;